import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ras_user")); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("ras_token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("ras_user", JSON.stringify(data));
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const applySession = useCallback((data) => {
    localStorage.setItem("ras_token", data.access_token);
    localStorage.setItem("ras_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.twofa_required) {
      return {
        twofaRequired: true,
        challengeToken: data.challenge_token,
        emailMasked: data.email_masked,
        expiresIn: data.expires_in,
      };
    }
    // Retrocompatibilità: se il backend restituisse direttamente il token.
    return { twofaRequired: false, user: applySession(data) };
  };

  const verify2fa = async (challengeToken, codice) => {
    const { data } = await api.post("/auth/verify-2fa", { challenge_token: challengeToken, codice });
    return applySession(data);
  };

  const resend2fa = async (challengeToken) => {
    const { data } = await api.post("/auth/resend-2fa", { challenge_token: challengeToken });
    return data;
  };

  const verifyBackupCode = async (challengeToken, codice) => {
    const { data } = await api.post("/auth/verify-backup-code", { challenge_token: challengeToken, codice });
    return applySession(data);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Errore logout", e); }
    localStorage.removeItem("ras_token");
    localStorage.removeItem("ras_user");
    setUser(null);
  };

  const can = (perm) => !!user && (user.permessi || []).includes(perm);

  return (
    <AuthContext.Provider value={{ user, loading, login, verify2fa, resend2fa, verifyBackupCode, logout, can, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
